<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import SkeletonLoader from "@components/SkeletonLoader.vue";
  import {Deferred as InertiaDeferred} from "@inertiajs/vue3";

  defineOptions({
    name: "ComponentStepper",
  });

  const props = defineProps({
    stepNumber: {
      type: String,
      required: true,
    },
    stepLabel: {
      type: String,
      default: null,
    },
    stepDescription: {
      type: String,
      default: null,
    },
    stepId: {
      type: String,
      default: null,
    },
    active: {
      type: String,
      default: null,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    disabledStepper: {
      type: Boolean,
      default: false,
    },
    lastStep: {
      type: Boolean,
      default: false,
    },
    stepWithItsOwnConfirmDialog: {
      type: Boolean,
      default: false,
    },
    optionalStepper: {
      type: Boolean,
      default: false,
    },
    isDefer: {
      type: Boolean,
      default: false,
    },
    deferPropName: {
      type: String,
      default: "",
    },
  });

  const emit = defineEmits(["click", "activateStep"]);

  const confirmDialog = ref();

  const isActive = computed(() => props.active === props.stepId);
  const isStepDescription = computed(() => props.stepDescription && props.stepDescription.length > 0);

  const goToNextStep = () => {
    emit("click", props.stepId);
  };

  const activeStep = () => {
    if (props.stepWithItsOwnConfirmDialog) {
      dialogConfirm();
    } else {
      confirmDialog.value.open();
    }
  };

  const dialogConfirm = () => {
    emit("activateStep", props.stepId);
  };

  defineExpose({
    activeStep,
  });
</script>

<template>
  <div :id="stepId" :data-test="getTestId('stepper')">
    <div
      class="ml-8"
      :class="{
        'border-l-2 border-mcred': isActive && !lastStep,
        'border-l-2 border-white': lastStep,
        'border-l-2 border-gray-200': !isActive && !lastStep,
      }"
    >
      <div class="-ml-9">
        <a
          v-if="disabledStepper"
          href="#"
          class="relative -top-8 flex min-h-18 items-start text-gray-700! no-underline! hover:bg-gray-100/75"
          :data-test="getTestId('stepper-active-step')"
          @click.prevent="activateStep"
        >
          <span class="flex h-9 items-center">
            <ComponentIcon v-if="warning" class="mt-8 ml-6 rounded-full bg-white text-2xl text-red-500">
              warning
            </ComponentIcon>

            <span
              v-else
              class="relative mt-10 ml-6 flex size-6 items-center justify-center rounded-full bg-gray-400 text-white"
            >
              {{ stepNumber }}
            </span>
          </span>

          <div class="mb-4 ml-4 min-w-0" :class="{'mt-4': isStepDescription, 'mt-6': !isStepDescription}">
            <div class="text-md tracking-wide text-gray-400">
              {{ stepLabel }}
              <span class="text-md text-mcred">aktivieren</span>
            </div>

            <span v-if="warning" class="text-xs text-gray-500">Dieser Bereich erfordert Ihre Aufmerksamkeit</span>

            <template v-else>
              <template v-if="isDefer">
                <inertia-deferred :data="deferPropName">
                  <template #fallback>
                    <skeleton-loader class-names="h-3.5 w-[600px]" />

                    <div class="w-full animate-pulse bg-gray-300 text-xs" />
                  </template>

                  <span class="text-xs text-gray-700" v-text="stepDescription" />
                </inertia-deferred>
              </template>

              <span v-else class="text-xs text-gray-700" v-text="stepDescription" />
            </template>
          </div>
        </a>

        <a
          v-else
          href="#"
          class="relative -top-8 flex min-h-18 items-start text-gray-700! no-underline! hover:bg-gray-100"
          :data-test="getTestId('stepper-next-step')"
          @click.prevent="goToNextStep"
        >
          <span class="flex h-9 items-center">
            <ComponentIcon v-if="warning" class="mt-8 ml-6 rounded-full bg-white text-2xl text-red-500">
              warning
            </ComponentIcon>

            <span
              v-else
              :class="{'bg-mcred': isActive, 'bg-gray-400': !isActive}"
              class="relative mt-10 ml-6 flex size-6 items-center justify-center rounded-full text-white"
            >
              {{ stepNumber }}
            </span>
          </span>

          <div class="mb-4 ml-4 min-w-0" :class="{'mt-4': isStepDescription, 'mt-6': !isStepDescription}">
            <div class="text-md black-800 tracking-wide">
              <span :class="{'font-semibold': isActive}">{{ stepLabel }}</span>
              <span v-if="optionalStepper" class="text-sm text-gray-500">(optional)</span>
            </div>

            <span v-if="warning" class="text-xs text-gray-700">Dieser Bereich erfordert Ihre Aufmerksamkeit</span>
            <template v-else>
              <template v-if="isDefer">
                <InertiaDeferred :data="deferPropName">
                  <template #fallback>
                    <SkeletonLoader class-names="h-3.5 w-[600px]" />

                    <div class="w-full animate-pulse bg-gray-300 text-xs" />
                  </template>

                  <span class="text-xs text-gray-700" v-text="stepDescription" />
                </InertiaDeferred>
              </template>

              <span v-else class="text-xs text-gray-700" v-text="stepDescription" />
            </template>
          </div>
        </a>

        <div v-if="isActive" :id="stepId + '-content'" class="-mt-6 ml-14 pr-6 pb-6">
          <slot name="content" />

          <div class="mt-5 mb-4">
            <slot name="actions" />
          </div>
        </div>
      </div>
    </div>

    <!-- TODO change with confirm dialog -->
    <ComponentDialog
      ref="confirmDialog"
      dialog-content="Möchten Sie den Step aktivieren?"
      dialog-type="confirm"
      primary-label="OK"
      :data-test="getTestId('confirmation')"
      @primary-button-clicked="dialogConfirm"
    />
  </div>
</template>
